import type { FC } from 'react';
import { cn } from '../lib/utils';
import { tokenizeSql, type SqlTokenKind } from './sql-tokens';

/** Highlight roles from DESIGN.md §8.9, mapped onto theme tokens. */
const TOKEN_CLASS: Record<SqlTokenKind, string> = {
  keyword: 'text-brand',
  function: 'font-medium text-foreground',
  literal: 'text-warning',
  reference: 'text-info',
  comment: 'text-muted-foreground italic',
  plain: 'text-[color:var(--z-text-secondary)]'
};

interface CodeBlockProps {
  code: string;
  /** `sql` highlights keywords, functions, literals, `@refs` and comments. */
  language?: 'sql' | 'plain';
  /** Accessible name for the block, e.g. "Generated SQL". */
  label?: string;
  className?: string;
}

/** Read-only, line-numbered code in the editor's monospace. */
export const CodeBlock: FC<CodeBlockProps> = ({
  code,
  language = 'plain',
  label,
  className
}) => (
  <pre
    aria-label={label}
    className={cn('overflow-x-auto py-3 font-mono text-[12px] leading-5', className)}
  >
    <code className="grid grid-cols-[40px_1fr]">
      {code.split('\n').map((line, number) => (
        // Lines and tokens have no identity beyond their position.
        <span key={number} className="contents">
          <span
            aria-hidden="true"
            className="pr-3 text-right text-[color:var(--z-text-disabled)] select-none"
          >
            {number + 1}
          </span>
          <span className="whitespace-pre">
            {language === 'sql' ? (
              tokenizeSql(line).map((token, at) => (
                <span key={at} className={TOKEN_CLASS[token.kind]}>
                  {token.text}
                </span>
              ))
            ) : (
              <span className={TOKEN_CLASS.plain}>{line}</span>
            )}
            {'\n'}
          </span>
        </span>
      ))}
    </code>
  </pre>
);
